import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Button, Avatar,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Divider, Paper, Snackbar, List, ListItem, ListItemAvatar, ListItemText,
  ListItemSecondaryAction, IconButton,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import StarIcon from '@mui/icons-material/Star';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import RateReviewIcon from '@mui/icons-material/RateReview';
import BadgeIcon from '@mui/icons-material/Badge';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../context/AuthContext';
import { getRoleLabel, getRoleColor } from '../../utils/permissions';
import db from '../../data/demoDatabase';
import KycVerificationDialog from '../../components/auth/KycVerificationDialog';
import StatCard from '../../components/common/StatCard';

const PRIMARY = '#1B365D';
const SECONDARY = '#00B894';

const KYC_STATUS_STYLES = {
  not_started: { bg: '#FFEBEE', color: '#C62828', label: 'Not Started', icon: GppMaybeIcon },
  in_progress: { bg: '#FFF8E1', color: '#F57F17', label: 'In Progress', icon: HourglassTopIcon },
  verified: { bg: '#E8F5E9', color: '#2E7D32', label: 'Verified', icon: VerifiedIcon },
  rejected: { bg: '#FFEBEE', color: '#C62828', label: 'Rejected', icon: GppMaybeIcon },
};

const TASK_ICONS = {
  social_share: ShareIcon,
  google_review: RateReviewIcon,
  ekyc_completed: VerifiedIcon,
};

export default function UserProfile() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [kycDialogOpen, setKycDialogOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [editForm, setEditForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    email: user?.email || '',
    icNumber: user?.icNumber || '',
  });

  const bump = () => setRefreshKey((k) => k + 1);
  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  // Fresh user from db
  const fullUser = useMemo(() => {
    if (!user?.id) return null;
    return db.users.getById(user.id);
  }, [user, refreshKey]);

  // Pending token transactions
  const pendingTxns = useMemo(
    () => (user?.id ? db.tokenTransactions.getPendingByUserId(user.id) : []),
    [user, refreshKey]
  );
  const pendingAmount = useMemo(
    () => pendingTxns.reduce((sum, t) => sum + (Number(t.amount) || 0), 0),
    [pendingTxns]
  );

  // Pending commission cases (for partners/agents)
  const pendingCommissionCases = useMemo(() => {
    if (!user?.id) return [];
    return db.cases.getAll().filter(
      (c) => c.referralOwnerId === user.id && c.commissionStatus === 'pending_kyc'
    );
  }, [user, refreshKey]);
  const pendingCommissionAmount = useMemo(
    () => pendingCommissionCases.reduce((sum, c) => sum + (Number(c.commissionAmount) || 0), 0),
    [pendingCommissionCases]
  );

  // Lifetime stats
  const stats = useMemo(() => {
    if (!user?.id) return { lifetime: 0, released: 0 };
    const allTxns = db.tokenTransactions.getByUserId(user.id);
    const lifetime = allTxns
      .filter((t) => ['reward', 'bonus', 'task_bonus'].includes(t.type))
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    return { lifetime };
  }, [user, refreshKey]);

  // Task bonus catalog + earned status
  const taskCatalog = useMemo(() => db.rewardConfigs.getActiveTaskBonuses(), [refreshKey]);
  const earnedTaskKeys = useMemo(() => {
    if (!user?.id) return new Set();
    const txns = db.tokenTransactions.getByUserId(user.id);
    const keys = new Set();
    txns.forEach((t) => {
      if (t.source?.startsWith('task:')) {
        keys.add(t.source.slice('task:'.length));
      }
    });
    return keys;
  }, [user, refreshKey]);

  const kycStatus = fullUser?.kycStatus || 'not_started';
  const kycMeta = KYC_STATUS_STYLES[kycStatus] || KYC_STATUS_STYLES.not_started;
  const KycIcon = kycMeta.icon;
  const roleLabel = getRoleLabel(user?.role);
  const roleColor = getRoleColor(user?.role);
  const isAdmin = user?.role === 'admin';

  // ── Handlers ──────────────────────────────────────
  const handleSaveProfile = () => {
    db.users.update(user.id, editForm);
    const fresh = db.users.getById(user.id);
    if (fresh) {
      const { password: _pw, ...safe } = fresh;
      updateUser?.(safe);
    }
    setEditOpen(false);
    showSnack('Profile updated');
    bump();
  };

  const handleKycComplete = (result) => {
    bump();
    if (result?.taskBonus && !result.taskBonus.alreadyEarned) {
      showSnack(`eKYC verified! +${result.taskBonus.amount} tokens credited`);
    } else {
      showSnack('eKYC verified successfully');
    }
    if (result?.released?.released > 0) {
      setTimeout(() => {
        showSnack(`${result.released.released} pending reward(s) released: +${result.released.amount} tokens`);
      }, 2000);
    }
  };

  const handleTaskClick = (task) => {
    if (earnedTaskKeys.has(task.key)) {
      showSnack('You have already earned this task bonus', 'info');
      return;
    }
    if (task.key === 'social_share') {
      navigate('/invite');
      return;
    }
    if (task.key === 'ekyc_completed') {
      setKycDialogOpen(true);
      return;
    }
    // Generic demo: mark done directly
    const result = db.engine.awardTaskBonus(user.id, task.key, user);
    if (result?.alreadyEarned) {
      showSnack('Already earned', 'info');
    } else if (result?.amount) {
      const fresh = db.users.getById(user.id);
      if (fresh) {
        const { password: _pw, ...safe } = fresh;
        updateUser?.(safe);
      }
      showSnack(`+${result.amount} tokens earned for "${result.task.label}"`);
      bump();
    }
  };

  if (!user) {
    return <Box sx={{ p: 3 }}><Alert severity="warning">Please log in to view your profile.</Alert></Box>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY, mb: 3 }}>
        My Profile
      </Typography>

      {/* Pending rewards banner */}
      {(pendingAmount > 0 || pendingCommissionAmount > 0) && kycStatus !== 'verified' && (
        <Alert severity="warning" icon={<HourglassTopIcon />} sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            You have rewards held pending KYC verification
          </Typography>
          <Typography variant="caption">
            {pendingAmount > 0 && <>• <strong>{pendingAmount} tokens</strong> from {pendingTxns.length} transaction(s)<br /></>}
            {pendingCommissionAmount > 0 && <>• <strong>RM {pendingCommissionAmount.toLocaleString()}</strong> commission across {pendingCommissionCases.length} case(s)<br /></>}
            Complete eKYC verification below to release them.
          </Typography>
        </Alert>
      )}

      {/* Header card */}
      <Card sx={{
        borderRadius: 3, mb: 3, overflow: 'hidden',
        background: 'linear-gradient(135deg, #1B365D 0%, #2C5282 100%)',
      }}>
        <CardContent sx={{ p: 4, color: 'white' }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Avatar sx={{
                width: 80, height: 80, backgroundColor: 'rgba(255,255,255,0.2)',
                fontSize: '1.8rem', fontWeight: 800,
              }}>
                {user.name?.split(' ').map((w) => w[0]).join('').slice(0, 2) || 'U'}
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{user.name}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>{user.email}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip label={roleLabel} size="small"
                  sx={{ backgroundColor: roleColor, color: 'white', fontWeight: 700 }} />
                <Chip
                  icon={<KycIcon sx={{ fontSize: 14, color: `${kycMeta.color} !important` }} />}
                  label={`KYC: ${kycMeta.label}`}
                  size="small"
                  sx={{ backgroundColor: kycMeta.bg, color: kycMeta.color, fontWeight: 700 }}
                />
                <Chip label={`ID: ${user.id}`} size="small"
                  sx={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }} />
              </Box>
            </Grid>
            {user.referralCode && (
              <Grid item sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>Referral Code</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{user.referralCode}</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Left column: Personal info + eKYC */}
        <Grid item xs={12} md={6}>
          {/* Personal Information */}
          <Card sx={{ borderRadius: 3, mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY }}>
                  Personal Information
                </Typography>
                <IconButton size="small" onClick={() => {
                  setEditForm({
                    name: fullUser?.name || '',
                    phone: fullUser?.phone || '',
                    email: fullUser?.email || '',
                    icNumber: fullUser?.icNumber || '',
                  });
                  setEditOpen(true);
                }}>
                  <EditIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Full Name</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{fullUser?.name || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{fullUser?.phone || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{fullUser?.email || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">IC Number</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{fullUser?.icNumber || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Member Since</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{fullUser?.joinDate || '—'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* eKYC Verification */}
          <Card sx={{ borderRadius: 3, mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, mb: 2 }}>
                eKYC Verification
              </Typography>
              {kycStatus === 'verified' ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Avatar sx={{ width: 72, height: 72, mx: 'auto', mb: 2, backgroundColor: '#E8F5E9' }}>
                    <VerifiedIcon sx={{ fontSize: 44, color: SECONDARY }} />
                  </Avatar>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: SECONDARY }}>
                    Identity Verified
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Verified via CTOS on {fullUser?.kycVerifiedAt ? new Date(fullUser.kycVerifiedAt).toLocaleDateString() : '—'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2, flexWrap: 'wrap' }}>
                    {fullUser?.idVerified && <Chip label="ID Verified" size="small" sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }} />}
                    {fullUser?.faceVerified && <Chip label="Face Matched" size="small" sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }} />}
                    {fullUser?.ctosVerified && <Chip label="CTOS Cleared" size="small" sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }} />}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Avatar sx={{ width: 72, height: 72, mx: 'auto', mb: 2, backgroundColor: '#FFF8E1' }}>
                    <GppMaybeIcon sx={{ fontSize: 44, color: '#F57F17' }} />
                  </Avatar>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Complete eKYC to unlock token earnings and commission payouts.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    3 steps: Upload IC → Face Capture → CTOS Check
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<BadgeIcon />}
                    onClick={() => setKycDialogOpen(true)}
                    sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: PRIMARY, fontWeight: 600 }}
                  >
                    Verify Identity Now
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right column: Stats + task bonuses */}
        <Grid item xs={12} md={6}>
          {/* Token summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <StatCard title="Available Tokens" value={fullUser?.tokenBalance || 0}
                subtitle="ready to redeem" icon={StarIcon} color="#FFD700" />
            </Grid>
            <Grid item xs={6}>
              <StatCard title="Lifetime Earned" value={stats.lifetime}
                subtitle="all tokens ever credited" icon={EmojiEventsIcon} color={SECONDARY} />
            </Grid>
            <Grid item xs={6}>
              <StatCard title="Pending KYC" value={pendingAmount}
                subtitle={`${pendingTxns.length} transaction(s)`} icon={HourglassTopIcon}
                color={pendingAmount > 0 ? '#F57F17' : '#90A4AE'} />
            </Grid>
            <Grid item xs={6}>
              <StatCard title="Pending Commission" value={`RM ${pendingCommissionAmount.toLocaleString()}`}
                subtitle={`${pendingCommissionCases.length} case(s)`} icon={AttachMoneyIcon}
                color={pendingCommissionAmount > 0 ? '#F57F17' : '#90A4AE'} />
            </Grid>
          </Grid>

          {/* Task Bonuses (hidden for admin) */}
          {!isAdmin && taskCatalog.length > 0 && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, mb: 0.5 }}>
                  Earn More Tokens
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Complete tasks to earn bonus tokens (one-time per task)
                </Typography>
                <List disablePadding>
                  {taskCatalog.map((task, idx) => {
                    const Icon = TASK_ICONS[task.key] || StarIcon;
                    const earned = earnedTaskKeys.has(task.key);
                    return (
                      <React.Fragment key={task.key}>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemAvatar>
                            <Avatar sx={{
                              backgroundColor: earned ? '#E8F5E9' : '#E3F2FD',
                              color: earned ? SECONDARY : '#1565C0',
                            }}>
                              <Icon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={<Typography variant="body2" sx={{ fontWeight: 600 }}>{task.label}</Typography>}
                            secondary={
                              <Chip
                                label={`+${task.tokens} tokens`}
                                size="small"
                                sx={{
                                  mt: 0.25,
                                  backgroundColor: '#FFF8E1',
                                  color: '#F57F17',
                                  fontWeight: 700,
                                  fontSize: '0.65rem',
                                }}
                              />
                            }
                          />
                          <ListItemSecondaryAction>
                            {earned ? (
                              <Chip
                                icon={<CheckCircleIcon sx={{ fontSize: 14, color: `${SECONDARY} !important` }} />}
                                label="Earned"
                                size="small"
                                sx={{ backgroundColor: '#E8F5E9', color: SECONDARY, fontWeight: 700 }}
                              />
                            ) : (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleTaskClick(task)}
                                sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.75rem' }}
                              >
                                {task.key === 'social_share' ? 'Share Now' :
                                 task.key === 'ekyc_completed' ? 'Verify' :
                                 'Mark Done'}
                              </Button>
                            )}
                          </ListItemSecondaryAction>
                        </ListItem>
                        {idx < taskCatalog.length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Edit profile dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>Edit Personal Information</Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Full Name" value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Phone" value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Email" type="email" value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="IC Number" value={editForm.icNumber}
                onChange={(e) => setEditForm({ ...editForm, icNumber: e.target.value })}
                helperText={kycStatus === 'verified' ? 'Changes to IC after KYC verification will require re-verification' : ''}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveProfile}
            sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: PRIMARY }}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* eKYC dialog */}
      <KycVerificationDialog
        open={kycDialogOpen}
        onClose={() => setKycDialogOpen(false)}
        onComplete={handleKycComplete}
      />

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}
          sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
